<template>
    <div class="container-fluid p-0">
        <div class="row m-0">
            <div class="col-12 p-0">
                <div class="login-card">
                    <div>
                        <div></div>
                        <div class="login-main">
                            <form class="theme-form" @submit.prevent="resetPassword">
                                <h4 class="text-center">Reset Password</h4>
                                <p class="text-center">Create a new password for your account</p>
                                
                                <div class="form-group">
                                    <label class="col-form-label">New Password</label>
                                    <div class="form-input position-relative">
                                        <input v-model="newPassword" :type="passwordType" class="form-control"
                                            placeholder="Enter new password" required minlength="8">
                                        <div class="show-hide">
                                            <span class="show" @click="togglePasswordVisibility('password')"></span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="form-group">
                                    <label class="col-form-label">Confirm Password</label>
                                    <div class="form-input position-relative">
                                        <input v-model="confirmPassword" :type="confirmPasswordType" class="form-control"
                                            placeholder="Confirm new password" required minlength="8">
                                        <div class="show-hide">
                                            <span class="show" @click="togglePasswordVisibility('confirm')"></span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div v-if="passwordMismatch" class="alert alert-danger" role="alert">
                                    Passwords do not match!
                                </div>
                                
                                <div class="form-group mb-0">
                                    <div class="text-end mt-3">
                                        <button class="btn btn-primary btn-block w-100" type="submit"
                                            :disabled="isSubmitting || passwordMismatch">
                                            {{ isSubmitting ? 'Resetting Password...' : 'Reset Password' }}
                                        </button>
                                    </div>
                                </div>
                                
                                <p class="mt-4 mb-0 text-center">
                                    <router-link class="ms-2" to="/auth/login">
                                        Back to Login
                                    </router-link>
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { createToaster } from "@meforma/vue-toaster";
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';

const baseUrl = process.env.VUE_APP_BASE_URL;
const toaster = createToaster({});
const router = useRouter();
const route = useRoute();

const resetToken = ref<string>("");
const newPassword = ref<string>("");
const confirmPassword = ref<string>("");
const isSubmitting = ref<boolean>(false);
const passwordType = ref<string>('password');
const confirmPasswordType = ref<string>('password');

const passwordMismatch = computed<boolean>(() => {
    return !!(newPassword.value && confirmPassword.value) && 
           newPassword.value !== confirmPassword.value;
});

onMounted(() => {
    // Get token from query parameters
    if (route.query.token) {
        resetToken.value = route.query.token as string;
    } else {
        // Redirect to forgot password page if no token is provided
        router.push('/auth/forgot-password');
        toaster.show("Password reset token is missing or expired", {
            theme: 'outline',
            position: 'top-right',
            icon: 'times',
            type: 'error',
            duration: 2000
        });
    }
});

function togglePasswordVisibility(field: string) {
    if (field === 'password') {
        passwordType.value = passwordType.value === 'password' ? 'text' : 'password';
    } else {
        confirmPasswordType.value = confirmPasswordType.value === 'password' ? 'text' : 'password';
    }
}

async function resetPassword() {
    // Validate inputs
    if (!newPassword.value || !confirmPassword.value) {
        toaster.show("Please fill in all fields", {
            theme: 'outline',
            position: 'top-right',
            icon: 'times',
            type: 'error',
            duration: 2000
        });
        return;
    }

    if (passwordMismatch.value) {
        toaster.show("Passwords do not match", {
            theme: 'outline',
            position: 'top-right',
            icon: 'times',
            type: 'error',
            duration: 2000
        });
        return;
    }

    if (newPassword.value.length < 8) {
        toaster.show("Password must be at least 8 characters long", {
            theme: 'outline',
            position: 'top-right',
            icon: 'times',
            type: 'error',
            duration: 2000
        });
        return;
    }

    isSubmitting.value = true;
    try {
        const response = await axios.post(`${baseUrl}api/user/reset-password-with-otp`, {
            resetToken: resetToken.value,
            newPassword: newPassword.value,
            confirmPassword: confirmPassword.value
        });

        toaster.show("Password has been reset successfully", {
            theme: 'outline',
            position: 'top-right',
            icon: 'check',
            type: 'success',
            duration: 3000
        });

        // Redirect to login page after successful password reset
        setTimeout(() => {
            router.push('/auth/login');
        }, 1500);
    } catch (error: any) {
        console.error('Error resetting password:', error);
        toaster.show(error.response?.data?.message || "Failed to reset password. Please try again.", {
            theme: 'outline',
            position: 'top-right',
            icon: 'times',
            type: 'error',
            duration: 2000
        });
    } finally {
        isSubmitting.value = false;
    }
}
</script> 